import { useState, type ReactNode } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { LockIcon } from "@icons";
import { BoardTruncatedText } from "./BoardTruncatedText";
import styles from "./Board.module.scss";

interface BoardColumnHeaderProps {
    titleId: string;
    title: string;
    icon?: ReactNode;
    count: number;
    totalCount?: number;
    actions?: ReactNode;
    refusalReason?: string;
    isRefusalShown: boolean;
    /** Create button, at the far right. */
    addButton?: ReactNode;
    /** Collapse toggle, left of the create button. */
    collapseToggle?: ReactNode;
}

const formatCount = (count: number, totalCount: number | undefined) =>
    totalCount === undefined || totalCount === count
        ? String(count)
        : `${count} of ${totalCount}`;

interface BoardRefusalLockProps {
    reason: string;
    /** Holds the tooltip open while the dragged card is over the column. */
    isShown: boolean;
}

/** Lock icon whose tooltip opens on hover, or while `isShown`. */
const BoardRefusalLock = ({
    reason,
    isShown,
}: Readonly<BoardRefusalLockProps>) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
        <Tooltip
            content={reason}
            open={isShown || isHovered}
            onOpenChange={setIsHovered}
        >
            <span role="img" aria-label={reason} className={styles.refusalIcon}>
                <LockIcon aria-hidden />
            </span>
        </Tooltip>
    );
};

/**
 * Icon, title and unique-item count ("3 of 7" while filters hide items) on
 * the left; `actions`, then the collapse toggle and create button on the
 * right. With a fine pointer the toggle and create button show on header
 * hover or keyboard focus inside it.
 */
export const BoardColumnHeader = ({
    titleId,
    title,
    icon,
    count,
    totalCount,
    actions,
    refusalReason,
    isRefusalShown,
    addButton,
    collapseToggle,
}: Readonly<BoardColumnHeaderProps>) => (
    <HStack
        as="header"
        gap={8}
        align="center"
        justify="between"
        className={styles.header}
    >
        <HStack gap={8} align="center" className={styles.headerLabel}>
            {Boolean(icon) && (
                <span aria-hidden className={styles.headerIcon}>
                    {icon}
                </span>
            )}
            <h3 id={titleId} className={styles.headerTitle}>
                <BoardTruncatedText>{title}</BoardTruncatedText>
                <span className={styles.headerCount}>
                    {formatCount(count, totalCount)}
                </span>
            </h3>
            {refusalReason !== undefined && (
                <BoardRefusalLock
                    reason={refusalReason}
                    isShown={isRefusalShown}
                />
            )}
        </HStack>
        {(Boolean(addButton) ||
            Boolean(collapseToggle) ||
            actions !== undefined) && (
            <HStack gap={4} align="center">
                {actions}
                {(Boolean(addButton) || Boolean(collapseToggle)) && (
                    <HStack
                        gap={4}
                        align="center"
                        className={styles.headerHoverActions}
                    >
                        {collapseToggle}
                        {addButton}
                    </HStack>
                )}
            </HStack>
        )}
    </HStack>
);
